import type { PatientIdentity } from '@/features/patients/types';
import type { StaffRef } from '@/features/orders/types';
import type { LimitAssessmentConfirmation } from '@/features/limit-billing/api';

export type BillStatus = 'open' | 'closed' | 'void';
export type BillLineKind = 'service' | 'medicine' | 'lab_test' | 'other';
export type PaymentMethod =
  'cash' | 'card' | 'e_wallet' | 'duitnow_qr' | 'deposit';

export const METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Cash',
  card: 'Card',
  e_wallet: 'E-wallet',
  duitnow_qr: 'DuitNow QR',
  deposit: 'Booking deposit',
};

export const LINE_KIND_LABEL: Record<BillLineKind, string> = {
  service: 'Service',
  medicine: 'Medicine',
  lab_test: 'Lab test',
  other: 'Other',
};

export interface BillLine {
  id: string;
  kind: BillLineKind;
  description: string;
  quantity: number;
  unitPriceSen: number;
  listUnitPriceSen: number | null;
  amountSen: number;
  covered: boolean;
  sourceId: string | null;
  /** RL-1637: `refill` = the medicine of an approved refill; null = the visit. */
  origin?: 'refill' | null;
  /**
   * RL-1742/1743: `stock` = priced from Stock › Prices when the bill was made,
   * `manual` = set at the counter, null = catalogue price or none yet.
   */
  priceSource?: 'stock' | 'manual' | null;
  /** RL-1743: why a set price was changed at the counter. */
  priceReason?: string | null;
  /** RL-1743: still at RM 0 and nobody priced it ("Needs a price"). */
  needsPrice?: boolean;
}

/** RL-1637: the bill of an approved refill (no visit behind it). */
export interface BillRefill {
  prescriptionId: string;
  prescriptionNumber: string | null;
  requestId: string | null;
  originalPrescription: { id: string; number: string | null } | null;
}

export interface BillPayment {
  id: string;
  method: PaymentMethod;
  amountSen: number;
  tenderedSen: number | null;
  changeSen: number | null;
  reference: string | null;
  receivedAt: string;
  /** Online payments are recorded by the system, so their staff id is null. */
  receivedBy: { id: string | null; fullName: string };
  online?: { provider: string } | null;
}

export type ApprovalKind = 'discount' | 'refund' | 'paid_out' | 'void';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export const APPROVAL_KIND_LABEL: Record<ApprovalKind, string> = {
  discount: 'Discount',
  refund: 'Refund',
  paid_out: 'Paid out',
  void: 'Void',
};

export interface BillApproval {
  id: string;
  kind: ApprovalKind;
  amountSen: number;
  reason: string;
  note: string | null;
  refundMethod: PaymentMethod | null;
  status: ApprovalStatus;
  requestedAt: string;
  requestedBy: StaffRef | null;
  decidedAt: string | null;
  decidedBy: StaffRef | null;
  decisionNote: string | null;
}

/** RL-1815 (decision D28): a note added beside a decided request. */
export interface ApprovalNote {
  id: string;
  text: string;
  at: string;
  by: StaffRef | null;
}

export interface Approval extends BillApproval {
  bill: {
    id: string;
    number: string;
    status: BillStatus;
    totalSen: number;
    paidSen: number;
    patient: { id: string; fullName: string };
    payments: {
      method: PaymentMethod;
      amountSen: number;
      receivedAt: string;
    }[];
  } | null;
  paidOut: { id: string; reason: string; receiptNumber: string | null } | null;
  /** RL-1815: correction notes, oldest first (the decision never changes). */
  correctionNotes?: ApprovalNote[];
}

export interface Bill {
  id: string;
  number: string;
  status: BillStatus;
  branch: { id: string; code: string; name: string };
  patient: {
    id: string;
    fullName: string;
    nationality: string;
    identity: PatientIdentity | null;
  };
  encounter: { id: string; closedAt: string | null; doctor: StaffRef } | null;
  queueNumber: number | null;
  /** RL-1637: set for a bill made when a doctor approved a refill. */
  refill?: BillRefill | null;
  payer: { id: string; code: string; name: string; kind: string } | null;
  memberNo: string | null;
  memberPrice: boolean;
  grossSen: number;
  panelSen: number;
  patientLinesSen: number;
  memberSavingSen: number;
  discountSen: number;
  taxSen: number;
  totalSen: number;
  paidSen: number;
  balanceSen: number;
  refundedSen: number;
  refundPendingSen: number;
  unpricedLines: number;
  receiptNumber: string | null;
  approvals: BillApproval[];
  duitNow: {
    id: string;
    amountSen: number;
    reference: string;
    payload: string;
    simulated: boolean;
    createdAt: string;
  } | null;
  claim: {
    id: string;
    amountSen: number;
    status: string;
    memberNo: string | null;
    createdAt: string;
  } | null;
  openedAt: string;
  openedBy: StaffRef | null;
  closedAt: string | null;
  closedBy: StaffRef | null;
  voidRequest: {
    requestedAt: string;
    requestedBy: StaffRef | null;
    reason: string | null;
    decidedAt: string | null;
    decidedBy: StaffRef | null;
    approved: boolean | null;
    note: string | null;
  } | null;
  version: number;
  lines: BillLine[];
  payments: BillPayment[];
}

export interface BillingSummary {
  openBills: number;
  openBalanceSen: number;
  collectedTodaySen: Record<PaymentMethod, number>;
  collectedTodayTotalSen: number;
  paymentsToday: number;
  toClaimFromPanelsSen: number;
  claimsCreatedToday: number;
  claimsCreatedTodaySen: number;
  approvalsWaiting: number;
}

export interface Receipt {
  receiptNumber: string;
  billNumber: string;
  branch: { id: string; code: string; name: string };
  patient: { fullName: string; identityLast4: string | null };
  payer: Bill['payer'];
  memberNo: string | null;
  lines: BillLine[];
  grossSen: number;
  panelSen: number;
  memberSavingSen: number;
  discountSen: number;
  taxSen: number;
  totalSen: number;
  payments: BillPayment[];
  refundedSen: number;
  closedAt: string;
  cashier: StaffRef | null;
}

export interface DrawerMovement {
  at: string;
  type: 'payment' | 'deposit' | 'refund' | 'paid_out';
  method: PaymentMethod;
  amountSen: number;
  what: string;
  /** Who took the payment or paid out; null for refunds and deposits. */
  by: StaffRef | null;
  /** RL-1822: who approved a refund or a paid-out above the limit. */
  approvedBy?: StaffRef | null;
  status?: ApprovalStatus;
}

export interface CashShift {
  id: string;
  number: string;
  branch: { id: string; code: string; name: string };
  status: 'open' | 'closed';
  floatSen: number;
  openedAt: string;
  openedBy: StaffRef;
  closedAt: string | null;
  closedBy: StaffRef | null;
  checkedBy: StaffRef | null;
  countedSen: number | null;
  differenceSen: number | null;
  differenceReason: string | null;
  takingsByMethod: Partial<Record<PaymentMethod, number>>;
  cashTakenSen: number;
  cashRefundsSen: number;
  paidOutSen: number;
  expectedSen: number | null;
  movements: DrawerMovement[];
  pendingPaidOuts: number;
}

export interface CashDrawer {
  paidOutLimitSen: number;
  current: CashShift | null;
  closed: CashShift[];
}

export interface FeeService {
  id: string;
  code: string;
  name: string;
  durationMinutes: number | null;
  priceSen: number | null;
  memberPriceSen: number | null;
  depositSen: number | null;
  branches: { id: string; name: string }[];
  allBranches: boolean;
  isActive: boolean;
  feeWarning: 'above_fee_schedule' | 'below_fee_schedule' | null;
}

export interface FeeSettings {
  serviceTax: {
    enabled: boolean;
    rateBps: number | null;
    sstRegistrationNo: string | null;
    thresholdSen: number | null;
    scopeConfirmed: boolean;
    last12MonthsSen: number;
    thresholdReached: boolean;
  };
  feeLimits: { minGpFeeSen: number | null; maxGpFeeSen: number | null };
}

export const DISCOUNT_REASONS = [
  { value: 'financial_hardship', label: 'Financial hardship' },
  { value: 'goodwill_complaint', label: 'Goodwill after a complaint' },
  { value: 'staff_or_family', label: 'Staff or family' },
  {
    value: 'price_matched_fee_schedule',
    label: 'Price matched to the fee schedule',
  },
] as const;

export const REFUND_REASONS = [
  { value: 'service_not_given', label: 'Service not given' },
  { value: 'charged_twice', label: 'Charged twice' },
  { value: 'wrong_price', label: 'Wrong price' },
  { value: 'panel_covered', label: 'Panel covered it after all' },
  { value: 'complaint_outcome', label: 'Complaint outcome' },
] as const;

export const REASON_LABEL: Record<string, string> = Object.fromEntries(
  [...DISCOUNT_REASONS, ...REFUND_REASONS].map((r) => [r.value, r.label]),
);

export interface PaymentInput {
  version: number;
  method: 'cash' | 'card' | 'e_wallet';
  amountSen: number;
  tenderedSen?: number;
  reference?: string;
  limitAssessment?: LimitAssessmentConfirmation;
}

export interface CloseBillInput {
  version: number;
  limitAssessment?: LimitAssessmentConfirmation;
}
